import {
  CalendarIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import * as Popover from "@radix-ui/react-popover";
import { joinClassNames } from "@utils/helpers";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import Calendar from "./calendar";
import { useSearchParams } from "next/navigation";
import { useWindowSize } from "@utils/hooks";
import { format } from "date-fns";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/popovers/dialog";
import { useTranslations } from "next-intl";

export default function DateFilter() {
  const windowSize = useWindowSize();
  const params = useSearchParams();
  const dateFrom = params.get("dateFrom");
  const dateTo = params.get("dateTo");
  const t = useTranslations("GenrePage.filters");
  const presetTranslations = useTranslations("GenrePage.filters.datepresets");

  const date = format(new Date(), "yyyy-MM-dd");
  const [year, month, day] = date.split("-").map((v) => parseInt(v));
  const [[date1, date2], setDate] = useState<[string | null, string | null]>([
    dateFrom || date,
    dateTo || date,
  ]);
  const [open, setOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] =
    useState<
      Parameters<
        ReturnType<typeof useTranslations<"GenrePage.filters.datepresets">>
      >[0]
    >("today");

  if (windowSize.width && windowSize.width <= 640) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger className="flex items-center gap-1 px-1 py-0.5 text-gray-600 bg-white hover:bg-gray-100 border border-gray-300 rounded-md transition-colors ease-in-out duration-300 cursor-default w-fit">
          <CalendarIcon className="h-5 w-5" />
          <span className="font-semibold">
            {date1 !== dateFrom || date2 !== dateTo || !date1 || !date2
              ? t("filterbydate")
              : t("eventson", {
                  datepreset: presetTranslations(selectedPreset),
                })}
          </span>
          <ChevronDownIcon
            className={joinClassNames(
              "h-5 w-5 will-change-transform transition-transform ease-in-out duration-300",
              open ? "rotate-180" : "rotate-0"
            )}
          />
        </DialogTrigger>
        <AnimatePresence>
          {open && (
            <DialogContent
              asChild
              forceMount
              handleClose={() => setOpen(false)}
              className="bottom-4 !m-2 !p-0 !rounded-3xl !bg-white !max-w-[90dvw] max-h-[85dvh] !text-indigo-800 !border-none"
            >
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                className="flex flex-col w-full"
                transition={{ duration: 0.2 }}
                variants={{
                  hidden: {
                    y: 200,
                    opacity: 0,
                  },
                  show: {
                    y: 0,
                    opacity: 1,
                  },
                }}
              >
                <div className="relative px-6 pt-6 pb-4 flex flex-col gap-3 justify-between flex-1 bg-indigo-500 rounded-t-3xl">
                  <div className="relative self-end w-8 h-8 rounded-full bg-white/10">
                    <DialogClose className="absolute top-2 left-2 w-4 h-4 text-white !font-bold">
                      <XMarkIcon />
                    </DialogClose>
                  </div>
                  <DialogHeader className="!text-left">
                    <DialogTitle className="!text-white text-xl !font-bold">
                      {t("filterbydate")}
                    </DialogTitle>
                    <CalendarIcon className="absolute top-0 left-4 w-24 h-24 text-white/10 -rotate-12" />
                  </DialogHeader>
                </div>
                <div className="relative pt-1 px-4 pb-4 max-h-[calc(85dvh-116px)] overflow-y-auto text-black">
                  <Calendar
                    date={date}
                    day={day}
                    month={month}
                    year={year}
                    date1={date1}
                    date2={date2}
                    setDate={setDate}
                    selectedPreset={selectedPreset}
                    setSelectedPreset={setSelectedPreset}
                    handleSubmit={() => setOpen(false)}
                  />
                </div>
              </motion.div>
            </DialogContent>
          )}
        </AnimatePresence>
      </Dialog>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className="flex items-center gap-1 px-1 py-0.5 text-gray-600 bg-white hover:bg-gray-100 border border-gray-300 rounded-md transition-colors ease-in-out duration-300 cursor-default w-fit">
        <CalendarIcon className="h-5 w-5" />
        <span className="font-semibold">
          {date1 !== dateFrom || date2 !== dateTo || !date1 || !date2
            ? t("filterbydate")
            : t("eventson", { datepreset: presetTranslations(selectedPreset) })}
        </span>
        <ChevronDownIcon
          className={joinClassNames(
            "h-5 w-5 will-change-transform transition-transform ease-in-out duration-300",
            open ? "rotate-180" : "rotate-0"
          )}
        />
      </Popover.Trigger>
      <AnimatePresence>
        {open && (
          <Popover.Portal forceMount>
            <Popover.Content
              className="relative z-40"
              sideOffset={15}
              align="end"
              alignOffset={-42}
              forceMount
            >
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                variants={{
                  hidden: {
                    opacity: 0,
                    y: "-1rem",
                    transition: { duration: 0.125 },
                  },
                  show: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.25 }}
                className="relative flex flex-col rounded-2xl px-4 py-2 bg-white text-black ring-1 ring-inset ring-gray-100 shadow min-w-[300px]"
              >
                <Calendar
                  date={date}
                  day={day}
                  month={month}
                  year={year}
                  date1={date1}
                  date2={date2}
                  setDate={setDate}
                  selectedPreset={selectedPreset}
                  setSelectedPreset={setSelectedPreset}
                />
              </motion.div>
            </Popover.Content>
          </Popover.Portal>
        )}
      </AnimatePresence>
    </Popover.Root>
  );
}
